import { PlanDealCode } from '../../types/composer';
import { Plan, ResolvedPlan } from '../../types/plans';

type FetchPlanGroup = (args: {
  planLengths: string[];
  dealCode?: string;
}) => Promise<Plan[]>;

type ResolvePlansArgs = {
  planLengths?: (string | number)[];
  dealCodes?: PlanDealCode[];
  defaultDealCode?: string;
  fetchPlans: FetchPlanGroup;
};

const normalizePlanLength = (value: string | number): string | undefined => {
  const planLength = Number(String(value).trim());
  if (!Number.isInteger(planLength) || planLength <= 0) return undefined;
  return String(planLength);
};

/** Returns one trimmed deal code per valid plan length. */
export const normalizeDealCodes = (
  dealCodes: PlanDealCode[] = [],
): Map<string, string> => {
  const normalized = new Map<string, string>();

  dealCodes.forEach(({ planLength, dealCode }) => {
    const length = normalizePlanLength(planLength);
    const code = String(dealCode || '').trim();
    if (length && code) normalized.set(length, code);
  });

  return normalized;
};

const uniquePlanLengths = (planLengths: (string | number)[] = []): string[] =>
  Array.from(
    new Set(planLengths.map(normalizePlanLength).filter(Boolean) as string[]),
  );

const tagPlans = (
  plans: Plan[] | undefined,
  dealCode?: string,
): ResolvedPlan[] =>
  (plans || []).map((plan) => ({
    ...plan,
    ...(dealCode ? { appliedDealCode: dealCode } : {}),
  }));

/**
 * A deal with `allowedPlans` makes `/v0/plans` ignore the requested lengths and
 * return its own, so each group keeps only the lengths it asked for. Without
 * this, one group's prices overwrite another group's cards when merged.
 */
const filterToLengths = (plans: Plan[], planLengths: string[]): Plan[] => {
  const requested = new Set(planLengths);
  return plans.filter((plan) => requested.has(String(plan.intervalLength)));
};

const sortPlans = (
  plans: ResolvedPlan[],
  requestedLengths: string[],
): ResolvedPlan[] => {
  const order = new Map(
    requestedLengths.map((planLength, index) => [Number(planLength), index]),
  );

  return plans.sort((a, b) => {
    const aOrder = order.get(a.intervalLength) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = order.get(b.intervalLength) ?? Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder || a.code.localeCompare(b.code);
  });
};

/**
 * Resolves plan prices using either the route's page-wide deal or Composer's
 * per-length deal map. A mapped tier overrides the page-wide deal; unavailable
 * tier deals fall back to list price so one bad campaign code cannot remove a
 * card from the grid.
 */
export const resolvePlans = async ({
  planLengths,
  dealCodes,
  defaultDealCode,
  fetchPlans,
}: ResolvePlansArgs): Promise<ResolvedPlan[]> => {
  const lengths = uniquePlanLengths(planLengths);
  const dealCodeByLength = normalizeDealCodes(dealCodes);

  // Single request: keep the API's own plan order, which existing plan blocks
  // render as-is.
  if (!dealCodeByLength.size) {
    const plans = await fetchPlans({
      planLengths: lengths,
      dealCode: defaultDealCode,
    });
    return tagPlans(plans, defaultDealCode);
  }

  const lengthsByDealCode = new Map<string | undefined, string[]>();
  lengths.forEach((planLength) => {
    const dealCode = dealCodeByLength.get(planLength) ?? defaultDealCode;
    const groupedLengths = lengthsByDealCode.get(dealCode) || [];
    groupedLengths.push(planLength);
    lengthsByDealCode.set(dealCode, groupedLengths);
  });

  const fetchGroup = async (
    groupedLengths: string[],
    dealCode?: string,
  ): Promise<Plan[]> => {
    try {
      const plans = await fetchPlans({ planLengths: groupedLengths, dealCode });
      return filterToLengths(plans, groupedLengths);
    } catch {
      return [];
    }
  };

  const groupedPlans = await Promise.all(
    Array.from(lengthsByDealCode.entries()).map(
      async ([dealCode, groupedLengths]) => {
        const plans = await fetchGroup(groupedLengths, dealCode);

        if (!dealCode) return tagPlans(plans);

        const returnedLengths = new Set(
          plans.map((plan) => String(plan.intervalLength)),
        );
        const missingLengths = groupedLengths.filter(
          (planLength) => !returnedLengths.has(planLength),
        );
        const fallbackPlans = missingLengths.length
          ? await fetchGroup(missingLengths)
          : [];

        return [...tagPlans(plans, dealCode), ...tagPlans(fallbackPlans)];
      },
    ),
  );

  const plansByCode = new Map<string, ResolvedPlan>();
  groupedPlans.flat().forEach((plan) => plansByCode.set(plan.code, plan));

  return sortPlans(Array.from(plansByCode.values()), lengths);
};
